import type { AdminTopupPack } from './content';
import type { AdminWorkspaceDetail, AdminWorkspaceItem } from './workspace';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const adminWorkspaceApi = {
  list: (params?: { search?: string; plan?: string }) =>
    api.get<AdminWorkspaceItem[]>('/api/admin/workspaces', { params }).then((r) => r.data),
  detail: (workspaceId: string) =>
    api.get<AdminWorkspaceDetail>(`/api/admin/workspaces/${workspaceId}`).then((r) => r.data),
  // Field is `planOverride` (not `override`) — `override` is a Kotlin soft
  // keyword and we don't want Jackson to silently miss it on the backend.
  updatePlan: (workspaceId: string, plan: string, planOverride: boolean) =>
    api
      .patch<AdminWorkspaceItem>(`/api/admin/workspaces/${workspaceId}/plan`, {
        plan,
        planOverride,
      })
      .then((r) => r.data),
  extendTrial: (workspaceId: string, extendDays: number) =>
    api
      .patch<AdminWorkspaceItem>(`/api/admin/workspaces/${workspaceId}/trial`, { extendDays })
      .then((r) => r.data),
  /**
   * Topup catalog — admin grant modal renders the picker from this.
   * Same shape `topupApi.catalog` returns to end-users; the admin
   * variant doesn't filter by plan (any pack is grantable).
   */
  listTopupPacks: () =>
    api.get<AdminTopupPack[]>('/api/admin/workspaces/topup-packs').then((r) => r.data),
  /**
   * Grant a topup pack to a workspace for free. Inserts a `topup_purchases`
   * row with `mollie_payment_id='admin-grant-{uuid}'` and
   * `price_paid_eur=0` so the audit trail is unambiguous.
   */
  grantTopup: (workspaceId: string, packKey: string) =>
    api
      .post<{
        purchaseId: string;
        packKey: string;
        unitsPurchased: number;
        molliePaymentId: string;
      }>(`/api/admin/workspaces/${workspaceId}/topups/grant`, { packKey })
      .then((r) => r.data),
};

/** Admin brand operations. */
export const adminBrandApi = {
  /**
   * Move a brand and every brand-scoped row (posts, analytics, GEO,
   * blog, engagement, …) to a different workspace.
   *
   * `dryRun: true` returns the row-count report without writing. Use
   * it to render a confirmation modal before committing. Same shape
   * is returned by the commit call (`dryRun: false`) with the actual
   * rowsAffected from the UPDATEs.
   */
  migrate: (
    brandId: string,
    body: { targetWorkspaceId: string; dryRun: boolean },
  ): Promise<AdminBrandMigrationResponse> =>
    api
      .post<AdminBrandMigrationResponse>(`/api/admin/brands/${brandId}/migrate`, body)
      .then((r) => r.data),
};

export interface AdminBrandMigrationResponse {
  brandId: string;
  brandName: string;
  sourceWorkspaceId: string;
  targetWorkspaceId: string;
  dryRun: boolean;
  counts: { table: string; rowsAffected: number }[];
  connectionsCleared: {
    postsPostingConnectionCleared: number;
    postsConnectionCleared: number;
    brandDefaultConnectionsDeleted: number;
  };
  warnings: string[];
}

/** Topup pack catalog row — backend `nl.ludicrous.socialsuite.model.TopupPack`. */
export interface VisualSettings {
  model: string;
  imageSize: string;
  numImages: number;
  availableModels: { id: string; label: string; description: string }[];
  availableImageSizes: { id: string; label: string }[];
}

export interface VisualModelResult {
  model: string;
  imageSize: string;
  seed: number | null;
  imageUrls: string[];
}

export interface VisualPreviewResult {
  prompt: string;
  results: VisualModelResult[];
}

export interface VisualPreviewOptions {
  prompt?: string;
  model?: string;
  imageSize?: string;
  numImages?: number;
  seed?: number;
  guidanceScale?: number;
  numInferenceSteps?: number;
  compareModels?: string[];
}
